import React, { useState } from "react"
import { Button, Select, Spin } from "antd"
import { useRequest } from "ahooks"
import { searchEmploymentInfosApi } from "@/services/apis/project"
import type { EmploymentInfo } from "../types"

/**
 * 用工信息搜索下拉框（已签约复盘 A1 工时填报错误「添加员工」）
 * 按工号、姓名或邮箱关键字模糊匹配（防抖 300ms），数据源 GET /workbenchApi/projects/employment-infos，
 * 选项展示 工号、姓名、邮箱、专业、公司；搜索无结果展示「未找到匹配员工」，失败可「重试」。
 */
const EmploymentInfoSearchSelect: React.FC<{
    onSelect: (info: EmploymentInfo) => void
    placeholder?: string
    className?: string
}> = ({ onSelect, placeholder, className }) => {
    const [options, setOptions] = useState<EmploymentInfo[]>([])
    const [keyword, setKeyword] = useState("")
    const [searched, setSearched] = useState(false)
    const [failed, setFailed] = useState(false)

    const { run: search, loading } = useRequest(searchEmploymentInfosApi, {
        manual: true,
        debounceWait: 300,
        onSuccess: (data) => {
            setOptions(data ?? [])
            setSearched(true)
            setFailed(false)
        },
        onError: () => {
            setOptions([])
            setSearched(true)
            setFailed(true)
        },
    })

    const handleSearch = (kw: string) => {
        const trimmed = kw.trim()
        setKeyword(trimmed)
        if (trimmed) {
            search(trimmed)
        } else {
            setOptions([])
            setSearched(false)
            setFailed(false)
        }
    }

    const notFoundContent = loading ? (
        <div className="py-3 text-center">
            <Spin size="small" />
        </div>
    ) : failed ? (
        <div className="flex items-center justify-center gap-2 py-3 text-sm text-red-500">
            用工信息加载失败
            <Button size="small" onClick={() => keyword && search(keyword)}>
                重试
            </Button>
        </div>
    ) : searched && keyword ? (
        <div className="py-3 text-center text-sm text-gray-400">未找到匹配员工</div>
    ) : (
        <div className="py-3 text-center text-sm text-gray-400">请输入工号、姓名或邮箱搜索</div>
    )

    return (
        <Select
            className={className}
            placeholder={placeholder ?? "输入工号、姓名或邮箱搜索员工"}
            showSearch
            allowClear
            filterOption={false}
            loading={loading}
            value={null}
            labelInValue={false}
            onSearch={handleSearch}
            notFoundContent={notFoundContent}
            options={options.map((info) => ({
                value: info.clerkCode,
                label: `${info.clerkCode} ${info.name}`,
                info,
            }))}
            optionRender={(opt) => {
                const info = (opt.data as { info?: EmploymentInfo }).info
                if (!info) return opt.label
                return (
                    <div className="flex flex-col">
                        <span className="text-sm text-gray-800">
                            {info.clerkCode} {info.name}
                            <span className="text-xs text-gray-400 ml-2">{info.email || "-"}</span>
                        </span>
                        <span className="text-xs text-gray-400">
                            {info.majorName || "-"} / {info.companyName || "-"}
                        </span>
                    </div>
                )
            }}
            onSelect={(_, option) => {
                const info = (option as { info?: EmploymentInfo }).info
                if (info) onSelect(info)
            }}
        />
    )
}

export default EmploymentInfoSearchSelect
